/**
 * TVH Scroll Animations
 * Fade + slide-up reveal as elements enter the viewport.
 * Targets both TVH custom sections and Horizon theme elements.
 */

(() => {
  const DURATION   = 600;   // animation duration in ms
  const DELAY_STEP = 80;    // stagger delay between siblings in ms
  const THRESHOLD  = 0.12;  // how much of the element must be visible to trigger
  const OFFSET     = 32;    // how many px elements start below their final position

  // Elements to animate — TVH custom classes + broad Horizon theme selectors
  const SELECTORS = [
    // TVH custom page elements
    '.tvh-hero',
    '.tvh-kicker',
    '.tvh-heading',
    '.tvh-card',
    '.tvh-step',
    '.tvh-feature',
    '.tvh-reward',
    '.tvh-reward-item',
    '.tvh-faq details',
    '.tvh-cta',
    '.tvh-soft-card',
    '.tvh-product',
    '.tvh-rate-list',
    '.tvh-not-accepted',
    // Horizon theme elements
    '.shopify-section',
    'section',
    '.featured-collection',
    '.collection-grid',
    '.product-card-wrapper',
    '.card-wrapper',
    '.banner',
    '.image-with-text',
    '.rich-text',
    '.multicolumn',
    '.collage',
    '.blog-articles',
    '.article-card',
    'footer-section',
    '.footer__content',
    'header ~ *',
  ].join(', ');

  // Inject base CSS into the document
  const injectCSS = () => {
    const style = document.createElement('style');
    style.id = 'tvh-scroll-anim-styles';
    style.textContent = `
      .tvh-anim-ready {
        opacity: 0;
        transform: translateY(${OFFSET}px);
        transition:
          opacity  ${DURATION}ms cubic-bezier(0.22, 1, 0.36, 1),
          transform ${DURATION}ms cubic-bezier(0.22, 1, 0.36, 1);
        will-change: opacity, transform;
      }
      .tvh-anim-visible {
        opacity: 1 !important;
        transform: translateY(0) !important;
      }
      /* Never hide above-the-fold content */
      .tvh-anim-skip {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
    `;
    document.head.appendChild(style);
  };

  // Check if an element is already visible on page load (above the fold)
  const isAboveFold = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  // Check if element is a child of an already-animated parent
  // to avoid double-animating nested elements
  const hasAnimatedParent = (el) => {
    let parent = el.parentElement;
    while (parent) {
      if (parent.classList.contains('tvh-anim-ready')) return true;
      parent = parent.parentElement;
    }
    return false;
  };

  // Apply stagger delay to siblings in the same grid/list
  const applyStagger = (el, index) => {
    const delay = index * DELAY_STEP;
    el.style.transitionDelay = `${delay}ms`;
  };

  // Set up the observer
  const setupObserver = () => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('tvh-anim-visible');
          // Remove delay after animation completes to avoid affecting hover states
          setTimeout(() => {
            entry.target.style.transitionDelay = '0ms';
          }, DURATION + (parseFloat(entry.target.style.transitionDelay) || 0));
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: THRESHOLD,
      rootMargin: '0px 0px -40px 0px'
    });

    return observer;
  };

  // Group siblings for stagger detection
  const getSiblingIndex = (el) => {
    const parent = el.parentElement;
    if (!parent) return 0;
    const animatedSiblings = Array.from(parent.children).filter(
      (child) => child.classList.contains('tvh-anim-ready')
    );
    return animatedSiblings.indexOf(el);
  };

  // Main init
  const init = () => {
    injectCSS();

    const observer = setupObserver();
    const elements = document.querySelectorAll(SELECTORS);
    const seen = new Set();

    elements.forEach((el) => {
      // Skip duplicates (element matched by multiple selectors)
      if (seen.has(el)) return;
      seen.add(el);

      // Skip if it has an already-animating parent
      if (hasAnimatedParent(el)) return;

      // Skip very small elements (icons, decorative)
      const rect = el.getBoundingClientRect();
      if (rect.width < 40 || rect.height < 20) return;

      if (isAboveFold(el)) {
        // Above fold — skip animation, show immediately
        el.classList.add('tvh-anim-skip');
      } else {
        // Below fold — set up animation
        el.classList.add('tvh-anim-ready');
        applyStagger(el, getSiblingIndex(el));
        observer.observe(el);
      }
    });
  };

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run for dynamically loaded content (e.g. Shopify section rendering API)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(init, 100);
  });

})();